import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffectiveIncludeImages } from "@/hooks/useUserData";
import { usePreferences } from "@/store/preferences";

// Matches PostResponse (list endpoint)
export interface Post {
  id: number;
  md5: string;
  file_ext: string;
  file_size: number | null;
  sample_url: string | null;
  preview_url: string | null;
  width: number | null;
  height: number | null;
  duration: number | null;
  score: number;
  fav_count: number;
  rating: string;
  tags: string[];
  artist_tags: string[];
  character_tags: string[];
  species_tags: string[];
  created_at: string | null;
}

// Matches PostDetail (detail endpoint)
export interface PostDetailData extends Post {
  file_url: string | null;
  copyright_tags: string[];
  meta_tags: string[];
  lore_tags: string[];
  general_tags: string[];
  sources: string[];
  description: string | null;
}

interface SearchPage {
  posts: Post[];
  page: number;
  limit: number;
  has_next: boolean;
}

async function fetchPosts(
  query: string,
  blacklist: string[],
  includeImages: boolean,
  page: number,
  limit = 40
): Promise<SearchPage> {
  const params = new URLSearchParams({ q: query, page: String(page), limit: String(limit) });
  if (blacklist.length > 0) {
    params.set("blacklist", blacklist.join(","));
  }
  // Always explicit — server treats the query arg as authoritative and
  // silently coerces to false if image indexing is disabled.
  params.set("include_images", includeImages ? "1" : "0");
  const res = await fetch(`/api/v1/posts?${params}`);
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  return res.json();
}

export function useSearch(query: string) {
  const { blacklist } = usePreferences();
  const includeImages = useEffectiveIncludeImages();

  return useInfiniteQuery({
    queryKey: ["posts", query, blacklist, includeImages],
    queryFn: ({ pageParam = 1 }) =>
      fetchPosts(query, blacklist, includeImages, pageParam as number),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_next ? last.page + 1 : undefined),
    enabled: true,
    staleTime: 30_000,
  });
}

export async function fetchPostDetail(id: number): Promise<PostDetailData> {
  const res = await fetch(`/api/v1/posts/${id}`);
  if (!res.ok) throw new Error(`Post not found: ${res.status}`);
  return res.json();
}

export interface AutocompleteSuggestion {
  name: string;
  category: number;
  category_name: string;
  post_count: number;
  implies: string[];
  // When the user's prefix matched an alias (e.g. typed "rainworld",
  // resolved to canonical "rain_world"), this is the antecedent that
  // matched. Null for direct prefix matches against the tags table.
  matched_alias: string | null;
}

export async function fetchAutocomplete(
  q: string,
  limit = 10,
  category?: number
): Promise<AutocompleteSuggestion[]> {
  if (!q) return [];
  const params = new URLSearchParams({ q, limit: String(limit) });
  if (category !== undefined) params.set("category", String(category));
  const res = await fetch(`/api/v1/autocomplete?${params}`);
  if (!res.ok) return [];
  return res.json();
}
